import GoogleIcon from '@mui/icons-material/Google';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import { useState } from 'react';

import {
  GoogleSignInCancelledError,
  useGoogleOAuth,
} from '@/features/identity/hooks/useGoogleOAuth';
import { useToast } from '@/hooks/useToast';

export interface GoogleSignInButtonProps {
  /** Called with the access token after successful OAuth flow. */
  onSuccess: (accessToken: string) => Promise<void>;
  /** Disables the button while processing. */
  disabled?: boolean;
  /** Button copy. Defaults to sign-in wording; recovery flows say "Continue with Google". */
  label?: string;
}

/**
 * A standalone Google Sign-In button for OAuth recovery flows.
 * Handles the OAuth popup, token retrieval, and success callback.
 */
export function GoogleSignInButton({
  onSuccess,
  disabled,
  label = 'Sign in with Google',
}: GoogleSignInButtonProps) {
  const { requestAccessToken, isConfigured } = useGoogleOAuth();
  const { showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const accessToken = await requestAccessToken();
      await onSuccess(accessToken);
    } catch (error) {
      // Dismissing the popup is not an error
      if (!(error instanceof GoogleSignInCancelledError)) {
        const message = error instanceof Error ? error.message : 'Google sign-in failed';
        showError(message);
      }
    } finally {
      // Cleared on EVERY path, including a successful token the caller then rejected: `onSuccess` can
      // resolve having reported a problem of its own, and leaving the button stuck on "Signing in..."
      // would deny the retry that problem is asking for.
      setIsLoading(false);
    }
  };

  if (!isConfigured) {
    return (
      <Tooltip title="Google Sign-In is not configured in this environment">
        <span>
          <Button
            variant="outlined"
            fullWidth
            disabled
            startIcon={<GoogleIcon />}
          >
            {label}
          </Button>
        </span>
      </Tooltip>
    );
  }

  return (
    <Button
      variant="outlined"
      fullWidth
      onClick={handleClick}
      disabled={disabled || isLoading}
      startIcon={
        isLoading ? <CircularProgress size={20} /> : <GoogleIcon />
      }
    >
      {isLoading ? 'Signing in...' : label}
    </Button>
  );
}
